import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/authContext";
import { getStorageKey } from "../../../utils/localStorageStateUniqueKeyGenerator";
import { checkFile } from "../../../utils/checkFile";
import { checkVideoFile } from "../../../utils/checkVideoFile";
import { uploadFileToS3 } from "../../../api/backend/uploadToS3";
import { apiFetchCurrentUserDetails } from "../../../api/backend/user";
import {
  getPresignedAdVideoUrl,
  getPresignedAdImgUrl,
} from "../../../api/backend/campaign";
import { apiCampaignPayment } from "../../../api/backend/auth";
import { periodChargeThresholdType } from "../../../constants/dropdownOptions";
import {
  handlePaymentMethodChange,
  onClearAllBillingAndPaymentFields,
  onClearState,
} from "../../../controller/watcherAndClearFunctions/billingWatchersAndClearFunctions";
import {
  validatePaymentMethod,
  validateExpirationMonth,
  validateExpirationYear,
  validateCreditCardNumber,
  validateFirstName,
  validateLastName,
  validateAddress1,
  validateCity,
  validatePostalCode,
  validateCountry,
  validateSelectedState,
  calculateValues,
} from "../../../controller/paymentsController";
import {
  pointsValueInCentsPerDollar,
  pointsPerDollar,
} from "../../../constants/rewardConstants";
import PlaceOrderSummary from "./placeOrderSummary";
import BillingForm from "./billingForm";
import { validateCardNotExpired } from "../../../utils/expirationDateValidator";
import { getCookie } from "../../../utils/cookies";
import ErrorMessageModal from "../../errorMessageModal/errorMessageModal";
import "./checkout.scss";

const Checkout = ({
  currentContextType,
  onResetStateOnCampaignSuccess, // Callback
}) => {
  // const currentContextType = "createCampaign"; // create campaign state used for the key context of local storage
  const checkoutRef = useRef(null); // Create a reference for the container
  const { currentMemberId } = useAuth(); // source id = current user for the campaign and updated globally
  const cloudFrontDomain = "media.skitsicon.com"; // Your CloudFront domain
  // Core user inputs
  const [paymentMethod, setPaymentMethod] = useState(""); // UseState to manage payment method directly
  const [expirationMonth, setExpirationMonth] = useState(() => {
    const storedCreditCardExpirationMonth = localStorage.getItem(
      getStorageKey("ccExpMonth", currentContextType)
    );
    return storedCreditCardExpirationMonth || ""; // Initialize from localStorage or default to empty
  });

  const [expirationYear, setExpirationYear] = useState(() => {
    // Initialize from localStorage or default to empty
    const storedCreditCardExpirationYear = localStorage.getItem(
      getStorageKey("ccExpYear", currentContextType)
    );
    return storedCreditCardExpirationYear || ""; // Initialize from localStorage or default to empty
  });
  const [selectedState, setSelectedState] = useState(() => {
    const storedState = localStorage.getItem(
      getStorageKey("selectedState", currentContextType)
    );
    console.log("Loaded selectedState from localStorage:", storedState);
    return storedState || "";
  });

  const [billingDetails, setBillingDetails] = useState(() => {
    const details = {
      firstName:
        localStorage.getItem(getStorageKey("firstName", currentContextType)) ||
        "",
      lastName:
        localStorage.getItem(getStorageKey("lastName", currentContextType)) ||
        "",
      address1:
        localStorage.getItem(getStorageKey("address1", currentContextType)) ||
        "",
      address2:
        localStorage.getItem(getStorageKey("address2", currentContextType)) ||
        "",
      city:
        localStorage.getItem(getStorageKey("city", currentContextType)) || "",
      postalCode:
        localStorage.getItem(getStorageKey("postalCode", currentContextType)) ||
        "",
      country:
        localStorage.getItem(getStorageKey("country", currentContextType)) ||
        "",
    };
    console.log("Loaded billingDetails from localStorage:", details);
    return details;
  });
  const [paymentType, setPaymentType] = useState(""); // impressions or clicks from last form
  const [creditCardNumber, setCreditCardNumber] = useState(() => {
    const storedCC = localStorage.getItem(
      getStorageKey("creditCardNumber", currentContextType)
    );
    console.log("Loaded creditCardNumber from localStorage:", storedCC);
    return storedCC || "";
  });
  const [totalBudget, setTotalBudget] = useState(() => {
    const storedTotalBudget = localStorage.getItem(
      getStorageKey("totalBudget", currentContextType)
    );
    console.log("Loaded totalBudget from localStorage:", storedTotalBudget);
    return storedTotalBudget ? parseFloat(storedTotalBudget) : 0; // Parse to float if value exists, default to 0 otherwise
  });
  const [costPerImpression, setCostPerImpression] = useState(0); // collected from last form
  const [costPerClick, setCostPerClick] = useState(0); // collected from last form
  const [selectedPeriod, setSelectedPeriod] = useState(""); // collected from last form
  const [impressionsOrClicks, setImpressionsOrClicks] = useState(0);
  const [selectedPeriodName, setSelectedPeriodName] = useState(""); // For period name
  const [fileUpload, setFileUpload] = useState(null); // collected from first form
  const [presignedUrlResponse, setPresignedUrlResponse] = useState(null); // collected from first form
  const [loading, setLoading] = useState(false);
  const [backendErrorMessage, setBackendErrorMessage] = useState("");

  // Metadata and auxiliary states
  const [imageMetaData, setImageMetaData] = useState({
    width: 0,
    height: 0,
    size: 0,
  });
  const [videoMetadata, setVideoMetadata] = useState({
    width: 0,
    height: 0,
    size: 0,
  });

  // Points and totals
  const [orderGrandTotal, setOrderGrandTotal] = useState(0); // Total before points
  const [orderTotalDue, setOrderTotalDue] = useState(0); // Total after points
  const [orderSubtotal, setOrderSubtotal] = useState(0); // Items cost
  const [userPointsBalance, setUserPointsBalance] = useState(0); // Backend points
  const [pointsToUse, setPointsToUse] = useState(0); // Points applied
  const [currentAvailablePoints, setCurrentAvailablePoints] = useState(0); // Fetched points

  // Derived values
  const maxOrderPoints = Math.min(
    userPointsBalance,
    Math.floor(orderGrandTotal * pointsPerDollar)
  );

  const [errors, setErrors] = useState({}); // Errors should be dynamic and tied to local storage, initialize it as an empty object
  const [merchantFee, setMerchantFee] = useState(() => {
    const storedFee = parseFloat(
      localStorage.getItem(getStorageKey("merchantFee", currentContextType))
    );
    console.log("Loaded merchantFee from localStorage:", storedFee);
    return storedFee || 0;
  });

  // payment check box terms and conditions agreement states
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);

  // using state to reset state, close the component, call back to the parent, placed in the success response
  const [campaignCreated, setCampaignCreated] = useState(false); // set to false so it won't trigger the useEffect on mount

  // Utility dropdown states
  const [isStateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [isPaymentMethodOpen, setPaymentMethodOpen] = useState(false);
  const [isYearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [isMonthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const [resetMonthDropdown, setResetMonthDropdown] = useState(false);
  const [resetYearDropdown, setResetYearDropdown] = useState(false);
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from(
    { length: 10 },
    (_, index) => currentYear + index
  ); // Generates years for the next 10 years

  const clearAllBillingAndPaymentFields = () => {
    onClearAllBillingAndPaymentFields(
      setCreditCardNumber,
      setExpirationMonth,
      setExpirationYear,
      setBillingDetails,
      currentContextType,
      getStorageKey
    );
  };

  // Updated useEffect with calculateValues
  useEffect(() => {
    const initialTotalBudget =
      parseFloat(
        localStorage.getItem(getStorageKey("totalBudget", currentContextType))
      ) || 0;
    const initialPaymentType = localStorage.getItem(
      getStorageKey("paymentType", currentContextType)
    );
    const initialCostPerImpression =
      parseFloat(
        localStorage.getItem(
          getStorageKey("costPerImpression", currentContextType)
        )
      ) || 0;
    const initialCostPerClick =
      parseFloat(
        localStorage.getItem(getStorageKey("costPerClick", currentContextType))
      ) || 0;
    const initialSelectedPeriod =
      localStorage.getItem(
        getStorageKey("paymentSubscription", currentContextType)
      ) || "";

    setTotalBudget(initialTotalBudget);
    setPaymentType(initialPaymentType);
    setCostPerImpression(initialCostPerImpression);
    setCostPerClick(initialCostPerClick);
    setSelectedPeriodName(
      periodChargeThresholdType.find(
        (period) => period.value === initialSelectedPeriod
      )?.name || ""
    );

    const derivedValues = calculateValues({
      totalBudget: initialTotalBudget,
      paymentType: initialPaymentType,
      costPerImpression: initialCostPerImpression,
      costPerClick: initialCostPerClick,
      merchantFee,
      userPointsBalance,
      pointsToUse,
      pointsValueInCentsPerDollar,
    });

    setImpressionsOrClicks(derivedValues.impressionsOrClicks);
    setOrderSubtotal(derivedValues.orderSubtotal);
    setOrderGrandTotal(derivedValues.orderGrandTotal);
    setOrderTotalDue(derivedValues.orderTotalDue);
    setPointsToUse(derivedValues.clampedPoints);
  }, [merchantFee, pointsToUse, userPointsBalance]);

  const handleSliderChange = (value) => {
    setErrors({});
    const updatedValues = calculateValues({
      totalBudget,
      paymentType,
      costPerImpression,
      costPerClick,
      merchantFee,
      userPointsBalance,
      pointsToUse: value,
      pointsValueInCentsPerDollar,
    });

    setPointsToUse(updatedValues.clampedPoints);
    setOrderTotalDue(updatedValues.orderTotalDue);

    if (parseFloat(updatedValues.orderTotalDue.toFixed(2)) === 0) {
      setMerchantFee(0);
      setPaymentMethod("points");
      clearState(); // Reset selectedState and billingDetails
      clearAllBillingAndPaymentFields(); // Clear all other billing and payment fields
      setResetMonthDropdown(true); // Trigger dropdown reset flag, like pulling a trigger on the gun to fire which resets state to true
      setResetYearDropdown(true);
    }
  };
  const onMonthDropdownReset = () => {
    setResetMonthDropdown(false); // Reset the flag after dropdown has been cleared or set it back to false, so Release the Trigger from the gun fire back to inital state, since we have no click we have to manually do it..
  };
  const onYearDropdownReset = () => {
    setResetYearDropdown(false); // Reset the flag after dropdown has been cleared or set it back to false, so Release the Trigger from the gun fire back to inital state, since we have no click we have to manually do it..
  };

  const onFieldChange = (fieldName, value) => {
    // Make sure currentContextType is available here (either via closure or as a parameter)
    console.log("currentContextType:", currentContextType);
    setBillingDetails((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    localStorage.setItem(getStorageKey(fieldName, currentContextType), value);
  };

  const clearState = () => {
    onClearState(
      getStorageKey,
      currentContextType,
      setSelectedState,
      setBillingDetails,
      setStateDropdownOpen
    );
  };
  const handleFileUpload = async () => {
    // If we're in update mode, skip file upload logic.
    if (currentContextType !== "createCampaign") {
      console.log("Update state detected – skipping file upload.");
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const adBlobString = localStorage.getItem("createCampaign_adBlob");
      if (!adBlobString) {
        throw new Error("No file information found in local storage.");
      }

      const fileDetails = JSON.parse(adBlobString);
      const { fileName, mimeType, fileSize, sourceId, campaignId, previewUrl } =
        fileDetails;
      let presignedUrlResponse;
      if (mimeType.startsWith("image/")) {
        checkFile(fileName, mimeType, fileSize);
        presignedUrlResponse = await getPresignedAdImgUrl(
          fileName,
          mimeType,
          fileSize,
          sourceId,
          campaignId
        );
      } else if (mimeType.startsWith("video/")) {
        checkVideoFile(fileName, mimeType, fileSize);
        presignedUrlResponse = await getPresignedAdVideoUrl(
          fileName,
          mimeType,
          fileSize,
          sourceId,
          campaignId
        );
      } else {
        throw new Error("Unsupported file type");
      }

      if (presignedUrlResponse) {
        const fileBlob = await fetch(previewUrl).then((r) => r.blob());
        await uploadFileToS3(
          presignedUrlResponse.presignedImgUrl ||
            presignedUrlResponse.presignedVideoUrl,
          fileBlob,
          async () => {
            // Successful upload, now handle success
            await handleUploadSuccess(fileDetails);
          },
          (error) => {
            console.error("Upload Error:", error);
            setBackendErrorMessage(
              "An error occurred during the upload process."
            );
            setLoading(false);
          }
        );
      }
    } catch (err) {
      console.error("Error during file upload process:", err);
      setBackendErrorMessage("An error occurred during the upload process.");
      setLoading(false);
    }
  };
  // Function to construct CloudFront URL based on file type
  const constructCloudFrontUrl = (fileName, sourceId, campaignId, isVideo) => {
    const filePath = isVideo
      ? `videos/${sourceId}/${campaignId}/${fileName}`
      : `images/${sourceId}/${campaignId}/${fileName}`;
    return `https://${cloudFrontDomain}/${filePath}`;
  };

  // Function to handle upload success and record ad campaign attributes
  // Function to handle upload success and generate CloudFront URL
  const handleUploadSuccess = async (fileDetails) => {
    const isVideo = fileDetails.mimeType.startsWith("video/");
    const cloudFrontUrl = constructCloudFrontUrl(
      fileDetails.fileName,
      fileDetails.sourceId,
      fileDetails.campaignId,
      isVideo
    );
    console.log("File uploaded successfully:", fileDetails.fileName);
    console.log("CloudFront URL:", cloudFrontUrl);
    setLoading(false);
  };
  const onPlaceOrder = async () => {
    // Validate terms agreement
    if (!agreeToTerms) {
      setTermsError(true); // Show error if terms are not agreed
      console.error("User did not agree to terms.");
      return; // Stop further execution
    }
    setTermsError(false); // Reset error if terms are agreed
    setAgreeToTerms(true);
    // Mark the function as async so I can use an await inside the function below to pass data to the next function
    // Convert grandTotal and other values into display format (dollars)
    const grandTotalUI = parseFloat(orderGrandTotal).toFixed(2); // Ensure consistent decimal formatting
    const subtotalUI = parseFloat(orderSubtotal).toFixed(2);
    const totalDueUI = parseFloat(orderTotalDue).toFixed(2);

    let paymentMethodType = "";
    if (pointsToUse > 0 && totalDueUI === "0.00") {
      paymentMethodType = "points";
    } else if (pointsToUse > 0 && parseFloat(totalDueUI) > 0) {
      paymentMethodType = "splitPayment";
    } else if (pointsToUse === 0 && parseFloat(totalDueUI) > 0) {
      paymentMethodType = "card";
    } else {
      console.error("Invalid payment method type");
      return;
    }

    console.log("Payment Method Type:", paymentMethodType);

    localStorage.setItem(
      getStorageKey("paymentMethodType", currentContextType),
      paymentMethodType
    );

    console.log("Values stored in localStorage:", {
      grandTotal: grandTotalUI,
      orderSubtotal: subtotalUI,
      orderTotalDue: totalDueUI,
      pointsToUse,
      paymentMethodType,
    });

    // Skip unnecessary validations for "points, all fields should be empty from the slider function anyways"
    if (paymentMethodType === "points") {
      clearState(); // Reset selectedState and billingDetails
      clearAllBillingAndPaymentFields(); // Clear all other billing and payment fields
      setResetMonthDropdown(true); // Trigger dropdown reset flag, like pulling a trigger on the gun to fire which resets state to true
      setResetYearDropdown(true);
    } else {
      // Validate fields for card or split payment method types
      const isPaymentMethodValid = validatePaymentMethod(
        currentContextType,
        setErrors
      );
      const isCreditCardNumberValid = validateCreditCardNumber(
        currentContextType,
        setErrors
      );
      const isExpirationMonthValid = validateExpirationMonth(
        currentContextType,
        setErrors
      );
      const isExpirationYearValid = validateExpirationYear(
        currentContextType,
        setErrors
      );
      const isFirstNameValid = validateFirstName(currentContextType, setErrors);
      const isLastNameValid = validateLastName(currentContextType, setErrors);
      const isAddress1Valid = validateAddress1(currentContextType, setErrors);
      const isCityValid = validateCity(currentContextType, setErrors);
      const isPostalCodeValid = validatePostalCode(
        currentContextType,
        setErrors
      );
      const isCountryValid = validateCountry(currentContextType, setErrors);
      const isSelectedStateValid = validateSelectedState(
        currentContextType,
        setErrors
      );

      console.log("Validation Results:", {
        isPaymentMethodValid,
        isExpirationMonthValid,
        isExpirationYearValid,
        isCreditCardNumberValid,
        isFirstNameValid,
        isLastNameValid,
        isAddress1Valid,
        isCityValid,
        isPostalCodeValid,
        isCountryValid,
        isSelectedStateValid,
      });
      // Check if any validation failed
      if (
        !isPaymentMethodValid ||
        !isExpirationMonthValid ||
        !isExpirationYearValid ||
        !isCreditCardNumberValid ||
        !isFirstNameValid ||
        !isLastNameValid ||
        !isAddress1Valid ||
        !isCityValid ||
        !isPostalCodeValid ||
        !isCountryValid ||
        !isSelectedStateValid
      ) {
        console.log("Validation failed for one or more fields.");
        return;
      }
      // All individual validations passed—now check if the card is expired
      if (!validateCardNotExpired(currentContextType)) {
        console.error("Card is expired.");
        setBackendErrorMessage(
          "Your card is expired. Please update the expiration date."
        );
        return;
      }
      console.log("Validation successful for all fields. Proceeding...");
    }
    // Only call file upload if we’re in create state.
    if (currentContextType === "createCampaign") {
      console.log(
        "onPlaceOrder: In create state – proceeding with file upload."
      );
      await handleFileUpload();
    } else {
      console.log("onPlaceOrder: In update state – skipping file upload.");
    }

    // Pass calculated values to `processCampaignPayment`
    await processCampaignPayment({
      orderGrandTotal: grandTotalUI,
      orderSubtotal: subtotalUI,
      orderTotalDue: totalDueUI,
      enableAutoPay: agreeToTerms,
      pointsToUse,
      paymentMethodType,
      currentAvailablePoints,
    });
  };

  const processCampaignPayment = async ({
    orderGrandTotal,
    orderSubtotal,
    orderTotalDue,
    pointsToUse,
    paymentMethodType,
    currentAvailablePoints,
    enableAutoPay,
  }) => {
    try {
      console.log("Starting processCampaignPayment...");
      if (paymentMethodType === "points") {
        console.log(
          "processCampaignPayment: Payment method is points. Setting localStorage keys."
        );
        localStorage.setItem(
          getStorageKey("paymentMethod", currentContextType),
          "points"
        );
        localStorage.setItem(
          getStorageKey("paymentMethodType", currentContextType),
          "points"
        );
        console.log(
          "Payment method explicitly set to 'points' for points-only payment."
        );
      }
      // Step 1: Clear old localStorage values for calculated fields
      const keysToClear = [
        getStorageKey("orderGrandTotal", currentContextType),
        getStorageKey("orderSubtotal", currentContextType),
        getStorageKey("orderTotalDue", currentContextType),
      ];
      keysToClear.forEach((key) => localStorage.removeItem(key));

      // Step 2: Store UI-calculated values directly into localStorage
      localStorage.setItem(
        getStorageKey("orderGrandTotal", currentContextType),
        orderGrandTotal.toString()
      );
      localStorage.setItem(
        getStorageKey("orderSubtotal", currentContextType),
        orderSubtotal.toString()
      );
      localStorage.setItem(
        getStorageKey("orderTotalDue", currentContextType),
        orderTotalDue.toString()
      );
      localStorage.setItem(
        getStorageKey("pointsToUse", currentContextType),
        pointsToUse.toString()
      );
      localStorage.setItem(
        getStorageKey("paymentMethodType", currentContextType.toString()),
        paymentMethodType
      );

      // Step 3: Retrieve and parse additional necessary data
      const adBlobString = localStorage.getItem("createCampaign_adBlob");
      let fileDetails; // Declare in the outer scope
      // Check only if we're in create mode
      if (currentContextType === "createCampaign" && !adBlobString) {
        throw new Error("Missing adBlobString in localStorage.");
      }
      if (currentContextType === "createCampaign") {
        // Assign to the outer fileDetails variable instead of redeclaring it.
        fileDetails = JSON.parse(adBlobString);
        if (!fileDetails.sourceId) {
          throw new Error("sourceId missing in fileDetails.");
        }
        // fileDetails is now available for use later in the function.
      } else {
        // In update mode, you might skip file upload entirely.
        console.log("Update state detected; skipping file upload.");
      }
      const adCategoryPromoString = localStorage.getItem(
        getStorageKey("adCategoryPromo", currentContextType)
      );

      if (!adCategoryPromoString) throw new Error("Missing Ad Category Promo.");

      const adCategoryPromoArray = JSON.parse(adCategoryPromoString);

      const selectedCategoryString = localStorage.getItem(
        getStorageKey("selectedCategory", currentContextType)
      );

      const selectedCategory = selectedCategoryString
        ? JSON.parse(selectedCategoryString)
        : null;

      const rawCreditCardNumber = localStorage.getItem(
        getStorageKey("creditCardNumber", currentContextType)
      );
      const creditCardNumber = String(rawCreditCardNumber); // Ensure it's a string
      // Declare necessary variables outside the campaignData object
      const costPerImpression = parseFloat(
        localStorage.getItem(
          getStorageKey("costPerImpression", currentContextType)
        )
      );
      const costPerClick = parseFloat(
        localStorage.getItem(getStorageKey("costPerClick", currentContextType))
      );
      const dailyCostLimit = parseFloat(
        localStorage.getItem(
          getStorageKey("dailyCostLimit", currentContextType)
        )
      );
      const dailyClicksLimit = parseInt(
        localStorage.getItem(
          getStorageKey("dailyClicksLimit", currentContextType)
        ),
        10
      );
      const dailyImpressionsLimit = parseInt(
        localStorage.getItem(
          getStorageKey("dailyImpressionsLimit", currentContextType)
        ),
        10
      );
      const totalBudgetClicks = parseInt(
        localStorage.getItem(
          getStorageKey("totalBudgetClicks", currentContextType)
        ),
        10
      );
      const totalBudgetImpressions = parseInt(
        localStorage.getItem(
          getStorageKey("totalBudgetImpressions", currentContextType)
        ),
        10
      );

      const totalBudget = parseInt(
        localStorage.getItem(getStorageKey("totalBudget", currentContextType))
      );
      const paymentType = localStorage.getItem(
        getStorageKey("paymentType", currentContextType)
      );
      const expireDateStr = localStorage.getItem(
        getStorageKey("campaignExpirationDate", currentContextType)
      );
      // Convert the string into a Date object
      const campaignEndDate = new Date(expireDateStr);

      const paymentSubscription = parseFloat(
        localStorage.getItem(
          getStorageKey("paymentSubscription", currentContextType)
        )
      );

      const campaignAdUrl = localStorage.getItem(
        getStorageKey("campaignAdUrl", currentContextType)
      );

      const updateCampaignIdFromCookie = getCookie("updateCampaign_campaignId");

      // Step 4: Build campaign data
      const campaignData = {
        campaignName: localStorage.getItem("createCampaign_campaignName"),
        campaignEndDate,
        paymentType,
        totalBudget,
        costPerClick,
        dailyCostLimit,
        dailyClicksLimit,
        dailyImpressionsLimit,
        totalBudgetClicks,
        totalBudgetImpressions,
        costPerImpression,
        paymentSubscription,
        creditCardNumber,
        campaignAdUrl,
        relationalSelectedCategoryId: selectedCategory?.categoryId || null,
        relationalSelectedCategoryName: selectedCategory?.categoryName || null,
        relationalAdPromoCategoryId:
          adCategoryPromoArray?.subcategoryId || null,
        relationalAdPromoName: adCategoryPromoArray?.subcategoryName || null,
        ...(currentContextType === "updateCampaign" && {
          updateThisCampaignId: updateCampaignIdFromCookie,
        }),
        // Conditionally add these keys only in create state:
        ...(currentContextType === "createCampaign" && {
          mediaAdUrl: `https://${cloudFrontDomain}/${
            fileDetails.mimeType.startsWith("video/") ? "videos" : "images"
          }/${fileDetails.sourceId}/${fileDetails.campaignId}/${encodeURIComponent(fileDetails.fileName)}`,
          commercialType: fileDetails.mimeType.startsWith("video/")
            ? "video"
            : "image",
          campaignId: fileDetails.campaignId, // Pass the campaignId to the backend
        }),
        billingDetails: {
          billingFirstName: localStorage.getItem(
            getStorageKey("firstName", currentContextType)
          ),
          billingLastName: localStorage.getItem(
            getStorageKey("lastName", currentContextType)
          ),
          billingAddressLine1: localStorage.getItem(
            getStorageKey("address1", currentContextType)
          ),
          billingAddressLine2: localStorage.getItem(
            getStorageKey("address2", currentContextType)
          ),
          billingCity: localStorage.getItem(
            getStorageKey("city", currentContextType)
          ),
          billingState: localStorage.getItem(
            getStorageKey("selectedState", currentContextType)
          ),
          billingZipCode: localStorage.getItem(
            getStorageKey("postalCode", currentContextType)
          ),
          billingCountry: localStorage.getItem(
            getStorageKey("country", currentContextType)
          ),
        },
      };

      console.log("Campaign Data:", campaignData);

      // Step 5: Build payment data
      const paymentData = {
        orderGrandTotal,
        orderSubtotal,
        orderTotalDue,
        pointsToUse,
        currentAvailablePoints,
        enableAutoPay,
        paymentMethodType,
        paymentMethod:
          paymentMethodType === "points"
            ? "points"
            : localStorage.getItem(
                getStorageKey("paymentMethod", currentContextType)
              ),
        ...(currentContextType === "createCampaign" && {
          sourceId: fileDetails.sourceId,
        }),
      };

      console.log("Payment Data:", paymentData);

      // Step 6: Combine into final payload
      const payload = {
        ...campaignData,
        ...paymentData,
      };
      console.log("Payload being sent to the server:", payload);

      // Step 7: API call
      const response = await apiCampaignPayment(payload);
      console.log("API Response:", response);
      if (response && response.success) {
        // Ensure 'response' exists and has 'success'
        console.log("Campaign payment processed successfully:", response);
      }
      setCampaignCreated(true); // Trigger state change
    } catch (error) {
      console.error("Error processing payment:", error.message);
    }
  };
  useEffect(() => {
    if (campaignCreated) {
      console.log("campaignCreated is true, invoking parent callback...");
      if (onResetStateOnCampaignSuccess) {
        onResetStateOnCampaignSuccess();
      }
    }
  }, [campaignCreated, onResetStateOnCampaignSuccess]);

  useEffect(() => {
    let calculatedImpressionsOrClicks = 0;

    // Calculate Impressions or Clicks based on the payment type
    if (paymentType === "impressions") {
      calculatedImpressionsOrClicks = Math.floor(
        totalBudget / costPerImpression
      );
    } else if (paymentType === "clicks") {
      calculatedImpressionsOrClicks = Math.floor(totalBudget / costPerClick);
    }

    setImpressionsOrClicks(calculatedImpressionsOrClicks);
    setSelectedPeriodName(periodChargeThresholdType[selectedPeriod] || ""); // Map period value to human-readable name
  }, [
    paymentType,
    totalBudget,
    costPerImpression,
    costPerClick,
    selectedPeriod,
  ]);

  const handleFetchUserPoints = async () => {
    try {
      const response = await apiFetchCurrentUserDetails(currentMemberId);
      const pointsBalance = response?.data?.currentPointsBalance || 0;

      setUserPointsBalance(pointsBalance); // Save backend points
      setCurrentAvailablePoints(pointsBalance); // Display total available points

      // If grandTotal is already calculated, update maxOrderPoints
      if (orderGrandTotal > 0) {
        const maxRedeemablePoints = Math.min(
          pointsBalance,
          Math.floor(orderGrandTotal * pointsPerDollar)
        );
        setPointsToUse(maxRedeemablePoints); // Set default points to max
      }
    } catch (error) {
      console.error("Error fetching user points:", error);
    }
  };

  const onPaymentMethodChange = (e) => {
    handlePaymentMethodChange(
      e,
      setPaymentMethod,
      setMerchantFee,
      currentContextType,
      getStorageKey
    );
  };

  useEffect(() => {
    handleFetchUserPoints();
  }, []);

  return (
    <div id="checkout" className="checkout-container" ref={checkoutRef}>
      <BillingForm
        billingDetails={billingDetails}
        setBillingDetails={setBillingDetails}
        onFieldChange={onFieldChange}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={onPaymentMethodChange}
        isPaymentMethodOpen={isPaymentMethodOpen}
        setPaymentMethodOpen={setPaymentMethodOpen}
        isMonthDropdownOpen={isMonthDropdownOpen}
        setMonthDropdownOpen={setMonthDropdownOpen}
        errors={errors}
        expirationMonth={expirationMonth}
        setExpirationMonth={setExpirationMonth}
        expirationYear={expirationYear}
        setExpirationYear={setExpirationYear}
        resetMonthDropdown={resetMonthDropdown}
        resetYearDropdown={resetYearDropdown}
        onMonthDropdownReset={onMonthDropdownReset}
        onYearDropdownReset={onYearDropdownReset}
        creditCardNumber={creditCardNumber}
        setCreditCardNumber={setCreditCardNumber}
        selectedState={selectedState}
        clearState={clearState}
        setSelectedState={setSelectedState}
        isYearDropdownOpen={isYearDropdownOpen}
        setYearDropdownOpen={setYearDropdownOpen}
        isStateDropdownOpen={isStateDropdownOpen}
        setStateDropdownOpen={setStateDropdownOpen}
        yearOptions={yearOptions}
        currentContextType={currentContextType}
      />
      {backendErrorMessage && (
        <ErrorMessageModal
          message={backendErrorMessage}
          onClose={() => setBackendErrorMessage("")} // Clear the error when modal is closed
        />
      )}
      <PlaceOrderSummary
        orderSubtotal={orderSubtotal}
        agreeToTerms={agreeToTerms}
        setAgreeToTerms={setAgreeToTerms}
        termsError={termsError}
        merchantFee={merchantFee}
        orderGrandTotal={orderGrandTotal}
        pointsToUse={pointsToUse}
        maxOrderPoints={maxOrderPoints}
        setTermsError={setTermsError}
        currentAvailablePoints={currentAvailablePoints}
        pointsValueInCentsPerDollar={pointsValueInCentsPerDollar}
        userPointsBalance={userPointsBalance}
        onSliderChange={handleSliderChange}
        onPlaceOrder={onPlaceOrder}
      />
    </div>
  );
};
export default Checkout;
